import { Fragment, useEffect, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { useDispatch, useSelector } from "react-redux";
import Image from "next/image";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { motion } from "framer-motion";

import { Button, BurgerButton, Disclosure } from "@chef/components";
import { useIntercom } from "@chef/hooks";
import {
  selectDrawerModalIsOpen,
  hideDrawerModal,
  useLogoutMutation,
  useMeQuery,
  toggleDrawerModal,
  startDebugging,
} from "@chef/state-management";
import { BRAND_PATHS, VISIBILITY } from "@chef/constants";
import { LoyaltyInfoCard } from "@chef/smart-components";
import { isEqualStrings } from "@chef/utils/equal";
import { useTrack } from "@chef/state-management/hooks";
import { useMobileApp } from "@chef/feature-mobile-app";

import { Logo } from "../../Logo";
import { useGetNavItems } from "../../../utils";

import { intl } from "./Drawer.Intl";

export const Drawer = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const [logout] = useLogoutMutation();
  const track = useTrack();

  const [logoTaps, setLogoTaps] = useState(0);

  const { data: isLoggedIn } = useMeQuery();
  const { isApp, appVersion, patchVersion } = useMobileApp();

  const isOpen = useSelector(selectDrawerModalIsOpen);

  useIntercom(!isOpen);

  useEffect(() => {
    router.events.on("routeChangeStart", handleClose);

    return () => {
      router.events.off("routeChangeStart", handleClose);
    };
  }, []);

  const handleClose = () => {
    dispatch(hideDrawerModal());
  };

  const handleShowLoginModal = () => {
    dispatch(hideDrawerModal());
  };

  // To trigger debug mode from logo click the logo must be repeatedly tapped
  // 7 times within 3 seconds
  const handleDebugModeClick = () => {
    if (logoTaps === 0) {
      setTimeout(() => {
        setLogoTaps(0);
      }, 3000);
    }

    setLogoTaps((t) => t + 1);

    if (logoTaps + 1 >= 7) {
      dispatch(hideDrawerModal());
      dispatch(startDebugging());
    }
  };

  const { drawerItems } = useGetNavItems();

  const DRAWER_ITEMS_BY_CATEGORY = drawerItems.reduce((acc, cur) => {
    if (!cur.category) {
      acc.push({ category: undefined, items: [cur] });
      return acc;
    }
    const idx = acc.findIndex((item) =>
      isEqualStrings(item.category, cur.category),
    );

    if (idx !== -1) {
      acc[idx].items.push(cur);
    } else {
      acc.push({ category: cur.category, items: [cur] });
    }

    return acc;
  }, [] as { category?: string; items: typeof drawerItems }[]);

  return (
    <Dialog
      unmount={false}
      onClose={handleClose}
      open={isOpen}
      className="relative z-50"
    >
      <DialogBackdrop className="fixed inset-0 bg-black/35 backdrop-blur-sm" />
      <div className="fixed inset-0 flex items-center justify-end w-screen">
        <DialogPanel className="flex flex-col justify-between w-full h-screen overflow-auto sm:w-96 bg-grey-3">
          <motion.div initial={false} animate={isOpen ? "open" : "closed"}>
            <div className="flex items-center justify-between gap-8 p-4 h-15 bg-primary">
              <div onClick={handleDebugModeClick}>
                <Logo variant="inverted" />
              </div>
              <BurgerButton
                id="close-menu-burger-button"
                onClick={() => dispatch(toggleDrawerModal())}
                className="rounded stroke-white hover:bg-grey-2/75 hover:stroke-black"
                title={intl.CLOSE_MENU_LABEL}
              />
            </div>
            {isLoggedIn && (
              <Link href={BRAND_PATHS.LOYALTY_HREF}>
                <LoyaltyInfoCard
                  className="bg-grey-2"
                  showInfo={false}
                  showNudge
                />
              </Link>
            )}

            <ul className="flex flex-col justify-between px-4 pt-2 pb-6">
              {DRAWER_ITEMS_BY_CATEGORY.map(({ category, items }, i) => {
                if (!category) {
                  return (
                    <Fragment key={i}>
                      {items.map((item) => {
                        return (
                          <li
                            className="relative border-b-1.5 border-grey-1"
                            key={`${item.href}-${item.label}`}
                            id={`${item.label}-drawer-link`}
                          >
                            <Link
                              href={item.href}
                              className="flex items-center py-4 text-base group"
                              passHref
                            >
                              {item.iconHref && (
                                <Image
                                  src={item.iconHref}
                                  alt={""}
                                  width={26}
                                  height={26}
                                  className="ml-1 mr-3"
                                />
                              )}
                              {item.empasize ? (
                                <strong>{item.label}</strong>
                              ) : (
                                <span>{item.label}</span>
                              )}
                              {item.campaignText && (
                                <div className="px-2 py-1 ml-2 text-xs bg-highlight">
                                  {item.campaignText}
                                </div>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                    </Fragment>
                  );
                }
                return (
                  <li
                    className="col-span-full md:col-span-1 border-b-1.5 border-grey-1"
                    key={category}
                  >
                    <nav>
                      <Disclosure
                        className="relative block py-4"
                        Title={() => (
                          <span className="text-base">{category}</span>
                        )}
                        defaultOpen={i === 0}
                      >
                        <ul className="flex flex-col -mt-4">
                          {items
                            .filter(
                              ({ visibility }) =>
                                visibility &
                                (isLoggedIn
                                  ? VISIBILITY.PRIVATE
                                  : VISIBILITY.PUBLIC),
                            )
                            .map((item) => {
                              return (
                                <li
                                  key={`${item.href}-${item.label}`}
                                  id={`${item.label}-drawer-link`}
                                  className="relative block ml-5"
                                >
                                  <Link
                                    href={item.href}
                                    className="flex items-center py-2 text-base"
                                  >
                                    {item.label}
                                  </Link>
                                </li>
                              );
                            })}
                        </ul>
                      </Disclosure>
                    </nav>
                  </li>
                );
              })}
            </ul>
          </motion.div>

          <div className="flex flex-col px-4">
            {isLoggedIn ? (
              <Button
                outlined
                className="mx-4 my-4 no-underline bg-white"
                onClick={() => {
                  logout();
                }}
              >
                {intl.LOG_OUT}
              </Button>
            ) : (
              <>
                <Link
                  onClick={() =>
                    track("linkClicked", {
                      affiliation: "Frontend process",
                      link_text: intl.CREATE_ACCOUNT,
                      link_placement: "drawer",
                      link_source: "code",
                      link_action: "click",
                      link_destination: BRAND_PATHS.SIGNUP_HREF,
                    })
                  }
                  href={BRAND_PATHS.SIGNUP_HREF}
                  passHref
                >
                  <Button primary className="mt-4 no-underline" full>
                    {intl.CREATE_ACCOUNT}
                  </Button>
                </Link>

                <Button
                  href={BRAND_PATHS.LOG_IN_URL}
                  onClick={handleShowLoginModal}
                  id="burger-menu-login-button"
                  className="my-4 no-underline bg-white"
                  full
                  outlined
                >
                  {intl.LOG_IN}
                </Button>
              </>
            )}
            {isApp && (
              <div className="mb-2 text-xs text-center">
                {[appVersion, patchVersion].filter(Boolean).join("+")}
              </div>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
